'use client';

import { useState } from 'react';
import { useCart } from '../context/cart-context';
import { CartEmpty } from './cart-empty';
import { CartItem } from './cart-item';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useRouter } from 'next/navigation';
import { formatPrice } from '@/lib/utils';
import { useCheckoutAuth } from '@/modules/checkout/hooks/use-checkout-auth';
import { CheckoutAuthModal } from '@/modules/checkout/components/checkout-auth-modal';

export function CartPage() {
  const { items, loading } = useCart();
  const router = useRouter();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { user, proceedToCheckout } = useCheckoutAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (items.length === 0) {
    return <CartEmpty />;
  }

  const subtotal = items.reduce((acc, item) => acc + item.price * item.qty, 0);
  const allCoins = items.length > 0 && items.every(it => {
    const sub = (it as any).subCategory as string | undefined;
    const cat = (it as any).category as string | undefined;
    const s1 = (sub || '').trim().toLowerCase();
    const s2 = (cat || '').trim().toLowerCase();
    return s1 === 'coin' || s1 === 'coins' || s2 === 'coin' || s2 === 'coins';
  });
  const totalCardsQty = items.reduce((acc, it) => {
    const sub = (it as any).subCategory as string | undefined;
    const cat = (it as any).category as string | undefined;
    const s1 = (sub || '').trim().toLowerCase();
    const s2 = (cat || '').trim().toLowerCase();
    const isCard = s1 === 'card' || s1 === 'cards' || s2 === 'card' || s2 === 'cards';
    return acc + (isCard ? it.qty : 0);
  }, 0);
  const shipping = allCoins ? 0 : totalCardsQty >= 5 ? 9 : totalCardsQty > 0 ? 3.5 : subtotal > 100 ? 0 : 10;
  const tax = Number((0.15 * subtotal).toFixed(2));
  const total = subtotal + shipping + tax;

  const handleProceedToCheckout = async () => {
    const result = await proceedToCheckout();
    if (result === 'show_auth_options') {
      setShowAuthModal(true);
    }
  };

  return (
    <div className="space-y-8 pb-10 pt-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold md:text-2xl">Shopping Cart</h1>
        <p className="text-muted-foreground">{items.length} items</p>
      </div>

      <div className="grid grid-cols-12 gap-8 lg:gap-6 md:grid-cols-1">
        <div className="col-span-8 md:col-span-1">
          <div className="space-y-4">
            {items.map(item => (
              <CartItem key={item.productId} item={item} />
            ))}
          </div>
        </div>

        <div className="col-span-4 md:col-span-1">
          <Card className="p-6">
            <h2 className="text-xl font-semibold">Order Summary</h2>
            <div className="mt-4 space-y-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Shipping</span>
                <span>{shipping === 0 ? 'Free' : formatPrice(shipping)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tax</span>
                <span>{formatPrice(tax)}</span>
              </div>
              <Separator />
              <div className="flex justify-between font-medium">
                <span>Total</span>
                <span>{formatPrice(total)}</span>
              </div>
              <Button
                className="w-full"
                size="lg"
                onClick={handleProceedToCheckout}
              >
                Proceed to Checkout
              </Button>
            </div>
          </Card>
        </div>
      </div>
      
      <CheckoutAuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
    </div>
  );
}
